/* fy-carousel — carrossel por scroll-snap com dots, sem dependências.
   Uso:
   <fy-carousel>
     <div class="fy-carousel__track" data-fy-track>
       <div class="fy-carousel__slide">…</div>
     </div>
     <div class="fy-carousel__dots" data-fy-dots></div>
     <button data-fy-prev>…</button> (opcional)
     <button data-fy-next>…</button> (opcional)
   </fy-carousel>
*/
(() => {
  if (customElements.get('fy-carousel')) return;

  class FyCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-fy-track]');
      this.dotsWrap = this.querySelector('[data-fy-dots]');
      this.prevButton = this.querySelector('[data-fy-prev]');
      this.nextButton = this.querySelector('[data-fy-next]');
      if (!this.track) return;

      this.slides = Array.from(this.track.children);
      if (this.slides.length < 2) {
        if (this.dotsWrap) this.dotsWrap.hidden = true;
        if (this.prevButton) this.prevButton.hidden = true;
        if (this.nextButton) this.nextButton.hidden = true;
        return;
      }

      this.activeIndex = -1;
      this.padStart = this.readPadStart();
      this.buildDots();
      this.bindArrows();
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.onScroll();
    }

    readPadStart() {
      const value = parseFloat(getComputedStyle(this.track).scrollPaddingLeft);
      return Number.isNaN(value) ? 0 : value;
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
    }

    onResize() {
      this.padStart = this.readPadStart();
      const active = this.slides[Math.max(0, this.activeIndex)];
      if (active) this.track.scrollTo({ left: active.offsetLeft - this.padStart });
      this.updateArrows();
    }

    bindArrows() {
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.goTo(this.activeIndex - 1));
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.goTo(this.activeIndex + 1));
      }
    }

    goTo(index) {
      const target = Math.max(0, Math.min(index, this.slides.length - 1));
      this.track.scrollTo({ left: this.slides[target].offsetLeft - this.padStart, behavior: 'smooth' });
    }

    buildDots() {
      if (!this.dotsWrap) return;
      this.dotsWrap.hidden = false;
      this.dotsWrap.textContent = '';
      this.dotButtons = this.slides.map((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'fy-carousel__dot';
        dot.setAttribute('aria-label', `Go to image ${index + 1} of ${this.slides.length}`);
        dot.addEventListener('click', () => this.goTo(index));
        this.dotsWrap.appendChild(dot);
        return dot;
      });
    }

    onScroll() {
      if (this.rafPending) return;
      this.rafPending = true;
      requestAnimationFrame(() => {
        this.rafPending = false;
        const position = this.track.scrollLeft + this.padStart;
        let nearest = 0;
        let nearestDistance = Infinity;
        this.slides.forEach((slide, index) => {
          const distance = Math.abs(slide.offsetLeft - position);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearest = index;
          }
        });
        this.setActive(nearest);
        this.updateArrows();
      });
    }

    updateArrows() {
      if (!this.prevButton && !this.nextButton) return;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const scrollable = maxScroll > 1;
      if (this.prevButton) {
        this.prevButton.hidden = !scrollable;
        this.prevButton.disabled = this.track.scrollLeft <= 1;
      }
      if (this.nextButton) {
        this.nextButton.hidden = !scrollable;
        this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 1;
      }
    }

    setActive(index) {
      if (index === this.activeIndex) return;
      this.activeIndex = index;
      if (!this.dotButtons) return;
      this.dotButtons.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        if (i === index) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }
  }

  customElements.define('fy-carousel', FyCarousel);
})();
